import { useEffect, useMemo, useRef, useState } from "react";
import type { CustomerItem } from "../../../../mocks/customers";
import { useCustomers } from "../../../../hooks/useCustomers";
import { useAnimals } from "../../../../hooks/useAnimals";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";

interface Props {
  onSelect: (customer: CustomerItem) => void;
  selectedCustomer: CustomerItem | null;
  onClear: () => void;
}

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function normalizeToken(value?: string) {
  return normalizeText(value).replace(/\s+/g, "");
}

export default function CustomerSearchBox({
  onSelect,
  selectedCustomer,
  onClear,
}: Props) {
  const { customers } = useCustomers();
  const { animals } = useAnimals();

  const [phoneQ, setPhoneQ] = useState("");
  const [nameQ, setNameQ] = useState("");
  const [kupeQ, setKupeQ] = useState("");
  const [siparisQ, setSiparisQ] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const queries = [phoneQ, nameQ, kupeQ, siparisQ].map((item) => item.trim());
    if (queries.every((item) => !item)) return [];

    const customerAnimalMap = new Map<
      number,
      {
        kupeNos: string[];
        siparisNos: string[];
      }
    >();

    for (const animal of animals) {
      const kupeToken = normalizeToken(getDisplayKupeNo(animal));
      const siparisToken = normalizeToken(getDisplaySiparisNo(animal));

      for (const share of animal.hisseler ?? []) {
        const owner = customers.find((customer) => {
          const sameById =
            typeof share.musteriId === "number" && customer.id === share.musteriId;

          const sameByPhone =
            normalizePhone(customer.telefon) &&
            normalizePhone(customer.telefon) === normalizePhone(share.telefon);

          const sameByName =
            normalizeText(customer.adSoyad) === normalizeText(share.musteriAdi);

          return sameById || sameByPhone || sameByName;
        });

        if (!owner) continue;

        const prev = customerAnimalMap.get(owner.id) ?? {
          kupeNos: [],
          siparisNos: [],
        };

        if (kupeToken && !prev.kupeNos.includes(kupeToken)) {
          prev.kupeNos.push(kupeToken);
        }

        if (siparisToken && !prev.siparisNos.includes(siparisToken)) {
          prev.siparisNos.push(siparisToken);
        }

        customerAnimalMap.set(owner.id, prev);
      }
    }

    const normalizedKupeQ = normalizeToken(kupeQ);
    const normalizedSiparisQ = normalizeToken(siparisQ);
    const normalizedPhoneQ = normalizePhone(phoneQ);
    const normalizedNameQ = normalizeText(nameQ);

    return customers
      .filter((customer) => {
        const phoneMatch = normalizedPhoneQ
          ? normalizePhone(customer.telefon).includes(normalizedPhoneQ)
          : true;

        const nameMatch = normalizedNameQ
          ? normalizeText(customer.adSoyad).includes(normalizedNameQ)
          : true;

        const customerAnimals = customerAnimalMap.get(customer.id) ?? {
          kupeNos: [],
          siparisNos: [],
        };

        const kupeMatch = normalizedKupeQ
          ? customerAnimals.kupeNos.includes(normalizedKupeQ)
          : true;

        const siparisMatch = normalizedSiparisQ
          ? customerAnimals.siparisNos.includes(normalizedSiparisQ)
          : true;

        return phoneMatch && nameMatch && kupeMatch && siparisMatch;
      })
      .slice(0, 12);
  }, [animals, customers, kupeQ, nameQ, phoneQ, siparisQ]);

  useEffect(() => {
    setShowDropdown(results.length > 0);
  }, [results]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleSelect(customer: CustomerItem) {
    onSelect(customer);
    setShowDropdown(false);
    setPhoneQ("");
    setNameQ("");
    setKupeQ("");
    setSiparisQ("");
  }

  if (selectedCustomer) {
    return (
      <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
        <div className="w-8 h-8 flex items-center justify-center text-green-600">
          <i className="ri-user-line text-xl" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 text-sm">
            {selectedCustomer.adSoyad}
          </p>
          <p className="text-xs text-gray-500">{selectedCustomer.telefon}</p>
        </div>

        <button
          onClick={onClear}
          className="text-xs text-gray-400 hover:text-red-500 cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors"
        >
          <i className="ri-close-line" />
          Değiştir
        </button>
      </div>
    );
  }

  return (
    <div ref={dropRef} className="relative space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <SearchInput
          icon="ri-hashtag"
          value={kupeQ}
          onChange={setKupeQ}
          placeholder="Küpe No ile ara..."
        />
        <SearchInput
          icon="ri-file-list-3-line"
          value={siparisQ}
          onChange={setSiparisQ}
          placeholder="Sipariş No ile ara..."
        />
        <SearchInput
          icon="ri-phone-line"
          value={phoneQ}
          onChange={setPhoneQ}
          placeholder="Telefon ile ara..."
        />
        <SearchInput
          icon="ri-user-search-line"
          value={nameQ}
          onChange={setNameQ}
          placeholder="Ad Soyad ile ara..."
        />
      </div>

      <p className="text-xs text-gray-400 ml-1">
        Küpe No ve Sipariş No araması birebir eşleşme ile çalışır.
      </p>

      {showDropdown && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {results.map((customer) => {
            const kalan = customer.toplamBorc - customer.toplamOdenen;

            return (
              <button
                key={customer.id}
                onClick={() => handleSelect(customer)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                  <i className="ri-user-line text-sm text-gray-500" />
                </div>

                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-gray-800">
                    {customer.adSoyad}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">
                    {customer.telefon}
                  </p>
                </div>

                <div className="text-right flex-shrink-0">
                  {kalan > 0 ? (
                    <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                      ₺{kalan.toLocaleString("tr-TR")} kalan
                    </span>
                  ) : (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      Ödenmiş
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SearchInput({
  icon,
  value,
  onChange,
  placeholder,
}: {
  icon: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        <i className={`${icon} text-gray-400 text-sm`} />
      </div>

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2.5 text-sm border-2 border-gray-200 focus:border-gray-400 rounded-lg outline-none bg-white transition-colors"
      />
    </div>
  );
}